import * as IoC from 'typescript-ioc';
import React from 'react';
import { action, observable, runInAction } from 'mobx';
import { FormattedMessage, IntlShape } from 'react-intl';
import { alertServerError, showNotification } from '../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../infrastructure/error-parsing';
import { InitialData } from '../../../infrastructure/form-viewmodel-controller';
import { AbstractFormViewModelController } from '../../../infrastructure/implementations/form-viewmodel-controller';
import { IReactIntlProvider } from '../../../infrastructure/react-intl-provider';
import { EmailTemplateTypes, IEmailTemplateField, IEmailTemplateFieldRequestParams, IEmailTemplatesDAO } from '../../../api/email-templates.dao';
import { ADD_TEXT, APPLY_TEXT } from '../../../constants/common-labels.constants';
import { FormFieldProps } from '../../_events/_manage-event/_connections/_meeting-connections-form/send-connections-email-form/send-connections-email-form.viewmodel';
import { TObject } from '../../../infrastructure/type-extensions';
import { IOption } from '../../global/select/select';
import { emailTemplateTypes } from '../../../constants/email-templates-label.constants';
import { getCommaSeparatedStringList } from '../../../infrastructure/utils/common.utils';
import {
    DELETE_EMAIL_TEMPLATE_PERMANENT_TEXT,
    DELETE_EMAIL_TEMPLATE_POPUP_TEXT,
    DELETE_EMAIL_TEMPLATE_UNDONE_TEXT,
    EMAIL_TEMPLATE_DELETED,
    EMAIL_TEMPLATE_UPDATED,
    NEW_EMAIL_TEMPLATE_ADDED
} from '../email-templates.text';
import { EmailTemplateFieldKey, configList } from './email-templates-form.config';
import { EMAIL_TEMPLATES_EVENTS } from '../../../tracking-service/tracking-values';
import TrackingServiceProvider from '../../../tracking-service/tracking-service-provider';
import { IGlobalStorage } from '../../../infrastructure/global-storage';
import { IAlertsService } from '../../../infrastructure/alerts';
import { Space } from '../../../constants/html-character-entities';
import { FORM_ARE_YOU_SURE_TO_TEXT, YES_DELETE_TEXT } from '../../_blueprints/conections-templates/connections-template-form/connection-template-form.texts';
import { transpileClassesToInlineStyles, transpileInlineStylesToClasses } from '../../global/rich-text-editor/utils/transpiler';

class EmailTemplatesFormVM {
    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    protected static createFormVM(initialData: InitialData = {}): AbstractFormViewModelController {
        return new class extends AbstractFormViewModelController {
            constructor() {
                super(initialData, 'SendConnectionsEmailFormViewmodel', configList);
            }
        }();
    }

    @observable
    private formDataCtrl: AbstractFormViewModelController;
    @observable
    private isLoading: boolean;
    @observable
    private isSaving = false;
    @observable
    private templateSnapshot: IEmailTemplateField | undefined;

    constructor(
        private readonly accountId: number,
        private readonly saveCompletedCallback: () => Promise<void>,
        private readonly deleteCompletedCallback: (id: string) => void,
        private readonly emailTemplateId?: string | undefined,
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
        @IoC.Inject private readonly emailTemplatesDAO: IEmailTemplatesDAO = IoC.Container.get(IEmailTemplatesDAO),
        @IoC.Inject protected readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject protected readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
    ) {

    }
    public get intl(): IntlShape { return this.intlProvider.getIntl(); }
    public get loading(): boolean { return this.isLoading; }
    public get saving(): boolean { return this.isSaving; }
    public get formVM(): AbstractFormViewModelController { return this.formDataCtrl; }
    public get isEditMode(): boolean { return !!this.emailTemplateId; }
    public get emailSnapshot(): IEmailTemplateField { return this.templateSnapshot; }

    public getFormFieldPropsByKey = (key: string): FormFieldProps => {
        return {
            value: this.formDataCtrl.getValue(key),
            setValue: value => this.formDataCtrl.setValue(key, value),
            setTouched: () => this.formDataCtrl.setValueTouched(key),
            ...this.formDataCtrl.getValueValidationState(key),
        } as FormFieldProps;
    };

    public getFormFields = (keys: TObject<string>): TObject<FormFieldProps> => {
        const formFields = {};
        for (const key of Object.values(keys)) {
            formFields[key] = this.getFormFieldPropsByKey(key);
        }
        return formFields;
    };

    public actionButtonText = (): string => {
        return this.isEditMode
            ? this.intl.formatMessage(APPLY_TEXT)
            : this.intl.formatMessage(ADD_TEXT)
            ;
    };

    public submitForm = (): Promise<boolean> => {
        if (!this.formDataCtrl.isVmValid()) {
            this.formDataCtrl.setVmTouched();
        } else {
            return this.callProperSubmitAction();
        }
    };

    public getEmailTypes = (): IOption[] => Object.values(EmailTemplateTypes).map(type => ({
        value: type,
        label: this.intl.formatMessage(emailTemplateTypes[type])
    }));

    public getSelectedEmails = (field: FormFieldProps): string[] => {
        return typeof field.value === 'string'
            ? field.value.split(',')
            : field.value as string[];
    };

    @action
    public initialize = async (): Promise<void> => {
        if (this.emailTemplateId) {
            await this.getTemplate(this.emailTemplateId);
        }
        this.formDataCtrl = EmailTemplatesFormVM.createFormVM(this.composeTemplateInitialProps());
    };

    @action
    public async getTemplate(emailTemplateId: string): Promise<void> {
        try {
            runInAction(() => {
                this.isLoading = true;
            });
            const result = await this.emailTemplatesDAO.getTemplateById({
                accountId: this.accountId,
                templateId: emailTemplateId,
            });
            runInAction(() => {
                // Since CKEditor works with classes, but data is stored with inline styles,
                // all CKEditor values need to be converted to the appropriate format
                this.templateSnapshot = {
                    ...result,
                    body: transpileInlineStylesToClasses(result.body)
                };
            });
        } catch (error) {
            EmailTemplatesFormVM.handleApiException(error);
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    public deleteConnectionEmailAction = (): void => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    <FormattedMessage {...FORM_ARE_YOU_SURE_TO_TEXT} />
                    <Space />
                    <span className='alert-message--danger'>
                        <FormattedMessage {...DELETE_EMAIL_TEMPLATE_POPUP_TEXT} />
                    </span>
                    <Space />
                    <FormattedMessage {...DELETE_EMAIL_TEMPLATE_PERMANENT_TEXT} />
                    <Space />
                    <span className='alert-message--danger'>
                        <FormattedMessage {...DELETE_EMAIL_TEMPLATE_UNDONE_TEXT} />
                    </span>
                </span>
            ),
            confirmButtonProps: {
                text: <FormattedMessage {...YES_DELETE_TEXT} />,
                modifier: 'confirm-warn',
                handler: this.deleteConnectionEmail,
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
    };

    private toEmailTemplateDTO = (): IEmailTemplateFieldRequestParams => {
        return {
            accountId: this.accountId,
            name: (this.formDataCtrl.getValue(EmailTemplateFieldKey.NAME) as string).trim(),
            type: this.formDataCtrl.getValue(EmailTemplateFieldKey.TYPE),
            description: (this.formDataCtrl.getValue(EmailTemplateFieldKey.DESCRIPTION) as string).trim(),
            to: getCommaSeparatedStringList(this.formDataCtrl.getValue(EmailTemplateFieldKey.TO) as string),
            replyTo: getCommaSeparatedStringList(this.formDataCtrl.getValue(EmailTemplateFieldKey.REPLY_TO) as string),
            cc: getCommaSeparatedStringList(this.formDataCtrl.getValue(EmailTemplateFieldKey.CC) as string),
            bcc: getCommaSeparatedStringList(this.formDataCtrl.getValue(EmailTemplateFieldKey.BCC) as string),
            subject: (this.formDataCtrl.getValue(EmailTemplateFieldKey.EMAIL_SUBJECT) as string).trim(),
            // Since CKEditor works with classes, but data is stored with inline styles,
            // all CKEditor values need to be converted to the appropriate format
            body: transpileClassesToInlineStyles(this.formDataCtrl.getValue(EmailTemplateFieldKey.EMAIL_BODY) as string)
        } as IEmailTemplateFieldRequestParams;
    };

    private callProperSubmitAction = (): Promise<boolean> => {
        return this.isEditMode
            ? this.editConnectionEmail()
            : this.createConnectionEmail();
    };

    @action
    private createConnectionEmail = async (): Promise<boolean> => {
        try {
            runInAction(() => {
                this.isSaving = true;
            });
            const dto = this.toEmailTemplateDTO();
            await this.emailTemplatesDAO.createEmailTemplate(dto);
            showNotification(
                this.intl.formatMessage(NEW_EMAIL_TEMPLATE_ADDED)
            );
            this.saveCompletedCallback();
            this.trackEventForMixPanel(EMAIL_TEMPLATES_EVENTS.CREATE_EMAIL_TEMPLATE);
            return true;
        } catch (e) {
            EmailTemplatesFormVM.handleApiException(e);
            return false;
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    };

    @action
    private editConnectionEmail = async (): Promise<boolean> => {
        try {
            runInAction(() => {
                this.isSaving = true;
            });
            const dto = {
                ...this.toEmailTemplateDTO(),
                id: this.templateSnapshot.id,
            };
            await this.emailTemplatesDAO.updateEmailTemplate(dto);
            showNotification(
                this.intl.formatMessage(EMAIL_TEMPLATE_UPDATED)
            );
            this.trackEventForMixPanel(EMAIL_TEMPLATES_EVENTS.EDIT_EMAIL_TEMPLATE);
            this.saveCompletedCallback();
            return true;
        } catch (e) {
            EmailTemplatesFormVM.handleApiException(e);
        }
    };

    @action
    private deleteConnectionEmail = async (): Promise<boolean> => {
        try {
            const dto = {
                accountId: this.accountId,
                templateId: this.emailTemplateId,
            };
            await this.emailTemplatesDAO.deleteEmailTemplate(dto);
            showNotification(
                this.intl.formatMessage(EMAIL_TEMPLATE_DELETED)
            );
            this.trackEventForMixPanel(EMAIL_TEMPLATES_EVENTS.DELETE_EMAIL_TEMPLATE);
            this.deleteCompletedCallback(this.emailTemplateId);
            return true;
        } catch (e) {
            EmailTemplatesFormVM.handleApiException(e);
        }
    };

    private composeTemplateInitialProps(): InitialData {
        return !this.templateSnapshot
            ? {}
            : {
                [EmailTemplateFieldKey.NAME]: this.templateSnapshot.name,
                [EmailTemplateFieldKey.DESCRIPTION]: this.templateSnapshot.description,
                [EmailTemplateFieldKey.TYPE]: this.templateSnapshot.type,
                [EmailTemplateFieldKey.TO]: this.templateSnapshot.to.join(','),
                [EmailTemplateFieldKey.REPLY_TO]: this.templateSnapshot.replyTo.join(','),
                [EmailTemplateFieldKey.CC]: this.templateSnapshot.cc.join(','),
                [EmailTemplateFieldKey.BCC]: this.templateSnapshot.bcc.join(','),
                [EmailTemplateFieldKey.EMAIL_SUBJECT]: this.templateSnapshot.subject,
                [EmailTemplateFieldKey.EMAIL_BODY]: this.templateSnapshot.body,
            };
    }

    private trackEventForMixPanel = (eventName: EMAIL_TEMPLATES_EVENTS): void => {
        this.trackingService.get().track(eventName, {
            'Account Name': this.globalStorage.getAccount()?.name,
            'Account ID': this.accountId,
            'Template Name': this.formDataCtrl.getValue(EmailTemplateFieldKey.NAME),
        });
    };
}

export { EmailTemplatesFormVM };
